import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, catchError, map, of } from "rxjs";
import { environment } from "../../environments/environment";
import { STORAGE_KEYS } from "../constants/storage-keys";
import { ApiResponseBody, ClientResponse } from "../models/api-response.model";
import { ToastrService } from "./toastr.service";

const UNKNOWN_ERROR_MESSAGE = "An unexpected error occurred. Please try again.";

@Injectable()
export class BaseService {
  protected readonly http = inject(HttpClient);
  protected readonly toastr = inject(ToastrService);
  protected readonly apiBaseUrl = environment.apiBaseUrl;

  protected get<T>(path: string): Observable<ClientResponse<T>> {
    return this.request<T>("GET", path);
  }

  protected post<T>(path: string, body: unknown): Observable<ClientResponse<T>> {
    return this.request<T>("POST", path, body);
  }

  protected put<T>(path: string, body: unknown): Observable<ClientResponse<T>> {
    return this.request<T>("PUT", path, body);
  }

  protected delete<T>(path: string): Observable<ClientResponse<T>> {
    return this.request<T>("DELETE", path);
  }

  protected postFormData<T>(path: string, formData: FormData): Observable<ClientResponse<T>> {
    const url = `${this.apiBaseUrl}${path}`;
    let headers = new HttpHeaders();
    const token = this.getAccessToken();

    if (token) {
      headers = headers.set("Authorization", `Bearer ${token}`);
    }

    return this.http.post<ApiResponseBody<T>>(url, formData, { headers }).pipe(
      map((response) => this.toClientResponse(response)),
      catchError((error: HttpErrorResponse) => of(this.handleError<T>(error)))
    );
  }

  private request<T>(method: string, path: string, body?: unknown): Observable<ClientResponse<T>> {
    const url = `${this.apiBaseUrl}${path}`;

    return this.http
      .request<ApiResponseBody<T>>(method, url, {
        body,
        headers: this.buildHeaders()
      })
      .pipe(
        map((response) => this.toClientResponse(response)),
        catchError((error: HttpErrorResponse) => of(this.handleError<T>(error)))
      );
  }

  private buildHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      "Content-Type": "application/json"
    });

    const token = this.getAccessToken();
    if (token) {
      headers = headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  }

  protected getAccessToken(): string | null {
    if (typeof localStorage === "undefined") {
      return null;
    }

    return localStorage.getItem(STORAGE_KEYS.accessToken);
  }

  private toClientResponse<T>(body: ApiResponseBody<T> | null | undefined): ClientResponse<T> {
    const response: ClientResponse<T> = {
      isSuccess: body?.success ?? false,
      message: body?.message ?? "",
      data: (body?.data ?? {}) as T,
      errors: body?.errors ?? []
    };

    this.showToast(response);
    return response;
  }

  private handleError<T>(error: HttpErrorResponse): ClientResponse<T> {
    const body = error.error as ApiResponseBody<T> | null;

    if (body && typeof body === "object" && "success" in body) {
      return this.toClientResponse(body);
    }

    const response: ClientResponse<T> = {
      isSuccess: false,
      message: UNKNOWN_ERROR_MESSAGE,
      data: {} as T,
      errors: []
    };

    this.toastr.danger(response.message);
    return response;
  }

  private showToast<T>(response: ClientResponse<T>): void {
    if (!response.message) {
      return;
    }

    if (response.isSuccess) {
      this.toastr.success(response.message);
    } else {
      this.toastr.danger(response.message);
    }
  }
}
